import artwork from "./iranFlagArtwork.json";

/**
 * Custom Iran flag rendered inline as a React component.
 * Lives in src/ — bundled by Next.js, never directly URL-accessible.
 *
 * CSS class names are prefixed with "mxir" to avoid any global collisions.
 */

// Enlarge the emblem so it remains recognizable at compact display sizes.
const COMPACT_MARKUP = artwork.markup.replace(
  '<g data-iran-emblem="true">',
  '<g data-iran-emblem="true" transform="translate(525 300) scale(2.2) translate(-525 -300)">',
);

/**
 * Renders the Iran flag as an inline SVG sized with CSS.
 * @param {string} size  — CSS length value (default "1.2rem")
 */
export default function IranFlagIcon({ size = "1.2rem", style, alt = "Iran flag" }) {
  return (
    <span
        role="img"
        aria-label={alt}
        style={{
          display: "inline-flex",
          alignItems: "center",
          width: size,
          aspectRatio: "7/4",
          flexShrink: 0,
          overflow: "hidden",
          borderRadius: 2,
          lineHeight: 1,
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: COMPACT_MARKUP }}
    />
  );
}

// ---------------------------------------------------------------------------
// FlagDisplay — drop-in replacement anywhere a flag emoji <span> is rendered.
// Returns the custom SVG for Iran (code "IR"); a Unicode emoji span otherwise.
// ---------------------------------------------------------------------------
const getEmojiFlag = (code) =>
  code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join("");

export function FlagDisplay({ code, size = "1.2rem", style }) {
  if (!code) return null;
  if (code.trim().toUpperCase() === "IR") {
    return <IranFlagIcon size={size} style={style} />;
  }
  return (
    <span style={{ fontSize: size, lineHeight: 1, flexShrink: 0, ...style }}>
      {getEmojiFlag(code)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// FlagImage — drop-in replacement for <img src="https://flagcdn.com/...">
// Returns the custom SVG for Iran ("IR"); an <img> from flagcdn for all others.
// Accepts the same style/alt/onError props as a regular <img>.
// ---------------------------------------------------------------------------
export function FlagImage({ code, style, alt, onError }) {
  if (!code) return null;
  if (code.trim().toUpperCase() === "IR") {
    return <IranFlagIcon style={style} alt={alt ?? "Iran flag"} />;
  }
  return (
    <img
      src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
      alt={alt ?? "Country Flag"}
      style={style}
      onError={onError}
    />
  );
}
